import React, { createContext, useContext, useRef, useCallback } from 'react';

/**
 * Simple data cache to persist page data between navigations
 * Prevents data from disappearing when switching pages
 */
const DataCacheContext = createContext(null);

// Cache entry with timestamp for staleness checking
const createCacheEntry = (data) => ({
  data,
  timestamp: Date.now()
});

export function DataCacheProvider({ children }) {
  // Use ref to persist cache across re-renders without causing re-renders
  const cacheRef = useRef({});

  // Default stale time: 5 minutes
  const DEFAULT_STALE_TIME = 5 * 60 * 1000;

  const getCache = useCallback((key, staleTime = DEFAULT_STALE_TIME) => {
    const entry = cacheRef.current[key];
    if (!entry) return null;

    // Check if data is stale
    const isStale = Date.now() - entry.timestamp > staleTime;
    if (isStale) {
      return { data: entry.data, isStale: true };
    }

    return { data: entry.data, isStale: false };
  }, []);

  const setCache = useCallback((key, data) => {
    cacheRef.current[key] = createCacheEntry(data);
  }, []);

  const invalidateCache = useCallback((key) => {
    if (key) {
      delete cacheRef.current[key];
    } else {
      cacheRef.current = {};
    }
  }, []);

  const updateCache = useCallback((key, updater) => {
    const entry = cacheRef.current[key];
    if (entry) {
      cacheRef.current[key] = createCacheEntry(updater(entry.data));
    }
  }, []);

  return (
    <DataCacheContext.Provider value={{ getCache, setCache, invalidateCache, updateCache }}>
      {children}
    </DataCacheContext.Provider>
  );
}

export function useDataCache() {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within DataCacheProvider');
  }
  return context;
}

/**
 * Hook for cached data fetching with automatic cache management
 * @param {string} cacheKey - Unique key for this data
 * @param {function} fetchFn - Async function to fetch data
 * @param {object} options - { staleTime, enabled }
 */
export function useCachedData(cacheKey, fetchFn, options = {}) {
  const { staleTime = 5 * 60 * 1000, enabled = true } = options;
  const { getCache, setCache } = useDataCache();
  const [data, setData] = React.useState(() => {
    const cached = getCache(cacheKey, staleTime);
    return cached?.data || null;
  });
  const [loading, setLoading] = React.useState(!data);
  const [error, setError] = React.useState(null);

  const refetch = React.useCallback(async (force = false) => {
    if (!enabled) return;

    // Check cache first (unless forced)
    if (!force) {
      const cached = getCache(cacheKey, staleTime);
      if (cached && !cached.isStale) {
        setData(cached.data);
        setLoading(false);
        return cached.data;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetchFn();
      setData(result);
      setCache(cacheKey, result);
      return result;
    } catch (err) {
      setError(err);
      console.error(`Error fetching ${cacheKey}:`, err);
    } finally {
      setLoading(false);
    }
  }, [cacheKey, fetchFn, enabled, staleTime, getCache, setCache]);

  // Initial fetch
  React.useEffect(() => {
    if (enabled) {
      const cached = getCache(cacheKey, staleTime);
      if (cached && !cached.isStale) {
        setData(cached.data);
        setLoading(false);
      } else {
        refetch();
      }
    }
  }, [cacheKey, enabled]);

  return { data, loading, error, refetch };
}

export default DataCacheContext;
